<script setup lang="ts">
import { onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

const route  = useRoute();
const router = useRouter();

const tab = ref<string>(String(route.name ?? 'schedule'));
watch(() => route.name, (n) => { if (n) tab.value = String(n); });

function go(name: string) {
  if (route.name !== name) router.push({ name });
}

// iPhone X+ reserves 34pt at the bottom for the home indicator. We probe
// safe-area-inset-bottom at mount and grow the nav so:
//   - the bar's CLICKABLE area reaches the bottom edge (active highlight
//     touches the bar's bottom — feels native)
//   - the icon+label sit ABOVE the home indicator (pushed up by inner
//     padding inside each v-btn — see <style> below)
//   - Vuetify's --v-layout-bottom reflects the real height (so the
//     schedule calendar's constraint is still correct)
// Halved compared to the first pass — felt like too much breathing room.
const BAR_BUTTONS_HEIGHT = 64;
const COMFORT_BUFFER     = 2;

// Total bottom inset: half of (home-indicator + comfort buffer). This is
// applied as padding-bottom INSIDE each v-btn so the active highlight
// still reaches the bar's bottom edge.
const innerBottomPad = ref(COMFORT_BUFFER);
const navHeight      = ref(BAR_BUTTONS_HEIGHT + COMFORT_BUFFER);

onMounted(() => {
  const probe = document.createElement('div');
  probe.style.cssText = 'position:absolute;bottom:0;height:env(safe-area-inset-bottom,0px);visibility:hidden;';
  document.body.appendChild(probe);
  const safeBottom = probe.getBoundingClientRect().height || 0;
  document.body.removeChild(probe);
  const halved = Math.round((safeBottom + COMFORT_BUFFER) / 2);
  innerBottomPad.value = halved;
  navHeight.value      = BAR_BUTTONS_HEIGHT + halved;
});
</script>

<template>
  <v-bottom-navigation
    v-model="tab"
    grow
    color="primary"
    bg-color="surface"
    :elevation="2"
    :height="navHeight"
    :style="{ '--lockin-nav-bottom-pad': innerBottomPad + 'px' }"
    class="lockin-bottom-nav"
  >
    <v-btn value="schedule" @click="go('schedule')">
      <v-icon>mdi-calendar-month</v-icon>
      <span>Rooster</span>
    </v-btn>
    <v-btn value="lia" @click="go('lia')">
      <v-icon>mdi-robot-happy-outline</v-icon>
      <span>LIA</span>
    </v-btn>
    <!-- Temporarily hidden
    <v-btn value="magister" @click="go('magister')">
      <v-icon>mdi-school-outline</v-icon>
      <span>Magister</span>
    </v-btn>
    -->

    <v-btn value="feed" @click="go('feed')">
      <v-icon>mdi-rss</v-icon>
      <span>Feed</span>
    </v-btn>
    <v-btn value="tips" @click="go('tips')">
      <v-icon>mdi-lightbulb-on-outline</v-icon>
      <span>Tips</span>
    </v-btn>
    <v-btn value="profile" @click="go('profile')">
      <v-icon>mdi-account-circle-outline</v-icon>
      <span>Profiel</span>
    </v-btn>
  </v-bottom-navigation>
</template>

<style scoped>
/* Pad the row inward by the left/right safe-area insets so icons clear
   rounded corners + landscape notch. Bottom inset is applied INSIDE
   each .v-btn (below) so the bar's clickable area — and the active
   highlight — still reach the bar's bottom edge. */
.lockin-bottom-nav :deep(.v-bottom-navigation__content) {
  padding-left:  calc(env(safe-area-inset-left,  0px) + 12px);
  padding-right: calc(env(safe-area-inset-right, 0px) + 12px);
  box-sizing: border-box;
}

/* Buttons fill the bar (so the active background touches the bottom),
   but their inner content is shifted up by --lockin-nav-bottom-pad so
   the icon+label sit above the home indicator zone. */
.lockin-bottom-nav :deep(.v-btn) {
  padding-bottom: var(--lockin-nav-bottom-pad, 0px);
  box-sizing: border-box;
}
</style>
